
import { Routes } from '@angular/router';
import { AuthGuard, LoginSuccessGuard } from '@uoa/auth';
import { CatchitAuthGuard, SuperAdminGuard, RequireAuthGuard } from './app/core/guards';
import { PendingLineCheckChangesGuard } from './app/core/guards/pending-line-check-changes.guard';


export const routes: Routes = [
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: 'error/:errorCode',
    loadChildren: () => import('./app/error-routing/error.routes').then((m) => m.errorRoutes),
  },
  {
    path: 'login',
    loadComponent: () => import('./app/pages/login/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'not-registered',
    canActivate: [RequireAuthGuard],
    loadComponent: () => import('./app/pages/not-registered/not-registered.page').then((m) => m.NotRegisteredPage),
  },
  {
    path: 'home',
    canActivate: [LoginSuccessGuard, RequireAuthGuard],
    loadComponent: () => import('./app/pages/home/home.page').then((m) => m.HomePage),
  },
  {
    path: 'protected',
    canActivate: [AuthGuard],
    data: { roles: ['user'] },
    loadChildren: () => import('./app/pages/protected/protected.routes').then((m) => m.protectedRoutes),
  },
  {
    path: 'profile',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/profile/profile.page').then((m) => m.ProfilePage),
  },
  {
    path: 'enter-data',
    canActivate: [AuthGuard, CatchitAuthGuard],
    canDeactivate: [PendingLineCheckChangesGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/enter-data/enter-data.page').then((m) => m.EnterDataPage),
  },
  {
    path: 'view-data',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/view-data/view-data.page').then((m) => m.ViewDataPage),
  },
  {
    path: 'admin',
    canActivate: [CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/admin.page').then((m) => m.AdminPage),
  },
  {
    path: 'graphs',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/graphs/graphs.page').then((m) => m.GraphsPage),
  },
  {
    path: 'export-data',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/export-data/export-data.page').then((m) => m.ExportDataPage),
  },
  {
    path: 'refresh-data',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/refresh-data/refresh-data.page').then((m) => m.RefreshDataPage),
  },
  {
    path: 'manage-data',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/manage-data/manage-data.page').then((m) => m.ManageDataPage),
  },
  {
    path: 'stored-submissions',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/stored-submissions/stored-submissions.page').then((m) => m.StoredSubmissionsPage),
  },
  {
    path: 'offline-enter-data',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadChildren: () => import('./app/pages/offline-enter-data/offline-enter-data.routes').then((m) => m.offlineEnterDataRoutes),
  },
  {
    path: 'traps-devices',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/traps-devices.page').then((m) => m.TrapsDevicesPage),
  },
  {
    path: 'account-settings',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/account-settings/account-settings.page').then((m) => m.AccountSettingsPage),
  },
  {
    path: 'reset-password',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/reset-password/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    path: 'email-settings',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/email-settings/email-settings.page').then((m) => m.EmailSettingsPage),
  },
  // Admin landing pages
  {
    path: 'areas',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/areas.page').then((m) => m.AreasPage),
  },
  {
    path: 'lines',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/lines.page').then((m) => m.LinesPage),
  },
  {
    path: 'people',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/people.page').then((m) => m.PeoplePage),
  },
  {
    path: 'csv-files',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/csv-files.page').then((m) => m.CsvFilesPage),
  },
  {
    path: 'custom-fields',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/custom-fields.page').then((m) => m.CustomFieldsPage),
  },
  {
    path: 'add-custom-field',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/custom-fields/pages/add-custom-field/add-custom-field.page').then((m) => m.AddCustomFieldPage),
  },
  {
    path: 'edit-custom-fields',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/custom-fields/pages/edit-custom-fields/edit-custom-fields.page').then((m) => m.EditCustomFieldsPage),
  },
  {
    path: 'line-selection',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/custom-fields/pages/line-selection/line-selection.page').then((m) => m.LineSelectionPage),
  },
  {
    path: 'category-area-assignment',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/custom-fields/pages/category-area-assignment/category-area-assignment.page').then((m) => m.CategoryAreaAssignmentPage),
  },
  {
    path: 'edit-categories',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/custom-fields/pages/edit-categories/edit-categories.page').then((m) => m.EditCategoriesPage),
  },
  {
    path: 'species-lures-device-type',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/species-lures-device-type.page').then((m) => m.SpeciesLuresDeviceTypePage),
  },
  {
    path: 'species-configuration',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/species-lures-device-type/species-configuration.page').then((m) => m.SpeciesConfigurationPage),
  },
  {
    path: 'lures-configuration',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/species-lures-device-type/lures-configuration.page').then((m) => m.LuresConfigurationPage),
  },
  {
    path: 'device-configuration',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/species-lures-device-type/device-configuration.page').then((m) => m.DeviceConfigurationPage),
  },
  {
    path: 'super-admin-actions',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions.page').then((m) => m.SuperAdminActionsPage),
  },
  {
    path: 'default-lures',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/areas/default-lures.page').then((m) => m.DefaultLuresPage),
  },
  {
    path: 'default-species',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/areas/default-species.page').then((m) => m.DefaultSpeciesPage),
  },
  {
    path: 'rename-areas',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/areas/rename-areas.page').then((m) => m.RenameAreasPage),
  },
  {
    path: 'download-csv',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/csv-files/download-csv.page').then((m) => m.DownloadCsvPage),
  },
  {
    path: 'create-area',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/areas/create-area.page').then((m) => m.CreateAreaPage),
  },
  {
    path: 'generate-gpx',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/areas/generate-gpx.page').then((m) => m.GenerateGpxPage),
  },
  {
    path: 'email-test',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions/email-test.page').then((m) => m.EmailTestPage),
  },
  {
    path: 'view-all-lines-in-area',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/areas/view-all-lines-in-area.page').then((m) => m.ViewAllLinesInAreaPage),
  },
  {
    path: 'upload-csv',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/csv-files/upload-csv.page').then((m) => m.UploadCsvPage),
  },
  {
    path: 'platform-information',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions/platform-information.page').then((m) => m.PlatformInformationPage),
  },
  {
    path: 'announcements',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/people/announcements.page').then((m) => m.AnnouncementsPage),
  },
  {
    path: 'add-users',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/people/add-users.page').then((m) => m.AddUsersPage),
  },
  {
    path: 'add-lines',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/lines/add-lines.page').then((m) => m.AddLinesPage),
  },
  {
    path: 'rename-lines',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/lines/rename-lines.page').then((m) => m.RenameLinesPage),
  },
  {
    path: 'move-devices-between-lines',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/lines/move-devices-between-lines.page').then((m) => m.MoveDevicesBetweenLinesPage),
  },
  {
    path: 'linked-devices',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/lines/linked-devices.page').then((m) => m.LinkedDevicesPage),
  },
  {
    path: 'enable-disable-lines',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/lines/enable-disable-lines.page').then((m) => m.EnableDisableLinesPage),
  },
  {
    path: 'website-message',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions/website-message.page').then((m) => m.WebsiteMessagePage),
  },
  {
    path: 'area-permissions',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/people/area-permissions.page').then((m) => m.AreaPermissionsPage),
  },
  {
    path: 'manage-emails',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/people/manage-emails.page').then((m) => m.ManageEmailsPage),
  },
  {
    path: 'special-messages',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/lines/special-messages.page').then((m) => m.SpecialMessagesPage),
  },
  {
    path: 'change-user-password',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions/change-user-password.page').then((m) => m.ChangeUserPasswordPage),
  },
  {
    path: 'change-user-details',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions/change-user-details.page').then((m) => m.ChangeUserDetailsPage),
  },
  {
    path: 'remove-stale-registration',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions/remove-stale-registration.page').then((m) => m.RemoveStaleRegistrationPage),
  },
  {
    path: 'line-permissions',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['admin', 'super-admin'] },
    loadComponent: () => import('./app/pages/admin/people/line-permissions.page').then((m) => m.LinePermissionsPage),
  },
  {
    path: 'super-user-admin',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/people/super-user-admin.page').then((m) => m.SuperUserAdminPage),
  },
  {
    path: 'usage-logs',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions/usage-logs.page').then((m) => m.UsageLogsPage),
  },
  {
    path: 'fix-line-check-device-links',
    canActivate: [AuthGuard, SuperAdminGuard],
    data: { roles: ['super-admin'] },
    loadComponent: () => import('./app/pages/admin/super-admin-actions/fix-line-check-device-links.page').then((m) => m.FixLineCheckDeviceLinksPage),
  },
  {
    path: 'rivers-tracks',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/rivers-tracks/rivers-tracks.page').then((m) => m.RiversTracksPage),
  },
  {
    path: 'tutorials',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/tutorials/tutorials.page').then((m) => m.TutorialsPage),
  },
  // Traps-Devices sub-pages
  {
    path: 'field-notes',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/field-notes/field-notes.page').then((m) => m.FieldNotesPage),
  },
  {
    path: 'add-devices',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/add-devices/add-devices.page').then((m) => m.AddDevicesPage),
  },
  {
    path: 'remove-devices',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/remove-devices/remove-devices.page').then((m) => m.RemoveDevicesPage),
  },
  {
    path: 'edit-device-properties',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/edit-device-properties/edit-device-properties.page').then((m) => m.EditDevicePropertiesPage),
  },
  {
    path: 'move-devices',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/move-devices/move-devices.page').then((m) => m.MoveDevicesPage),
  },
  {
    path: 'device-order',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/device-order/device-order.page').then((m) => m.DeviceOrderPage),
  },
  {
    path: 'default-baits',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/default-baits/default-baits.page').then((m) => m.DefaultBaitsPage),
  },
  {
    path: 'bulk-change-device-type',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/bulk-change-device-type/bulk-change-device-type.page').then((m) => m.BulkChangeDeviceTypePage),
  },
  {
    path: 'reinstate-devices',
    canActivate: [AuthGuard, CatchitAuthGuard],
    data: { roles: ['user'] },
    loadComponent: () => import('./app/pages/traps-devices/pages/reinstate-devices/reinstate-devices.page').then((m) => m.ReinstateDevicesPage),
  },
  {
    path: 'sign-up',
    loadComponent: () => import('./app/pages/sign-up/sign-up.page').then((m) => m.SignUpPage),
  },
  {
    path: 'forgotten-password',
    loadComponent: () => import('./app/pages/forgotten-password/forgotten-password.page').then((m) => m.ForgottenPasswordPage),
  },
  {
    path: '**',
    redirectTo: '/home',
  },
];
